import { defineStore } from "pinia";
import { FilterItemTypes } from "./types";
import { computed, ref, toRaw } from 'vue';
export const useFilterStore = defineStore('filterStore', () => {
    const config = ref({});
    const values = ref({});
    const instance = ref(0);
    const active = computed(() => Object.keys(values.value).length > 0);
    const enabled = ref(true);
    const loaded = ref(false);
    const items = ref([]);
    const sort = ref({
        default: 'hot',
        type: 'hot',
        types: {},
    });
    const available = computed(() => {
        const available = {};
        for (const item of items.value) {
            if (item.type === FilterItemTypes.Flag) {
                available[item.name] = item.available;
            }
            if (item.type === FilterItemTypes.Range) {
                available[item.name] = toRaw(item.available);
            }
            if (item.type === FilterItemTypes.List || item.type === FilterItemTypes.FilterableList) {
                for (const option of item.options) {
                    if (!available[item.name]) {
                        available[item.name] = {};
                    }
                    available[item.name][option.name] = option.available;
                }
            }
        }
        return available;
    });
    function disable() {
        enabled.value = false;
    }
    function setLoaded(isLoaded = true) {
        loaded.value = isLoaded;
    }
    function setInitialState(state) {
        config.value = state.config;
        values.value = state.values;
        setValues(state.values);
    }
    function setValues(filterValues) {
        for (const item of items.value) {
            let value = filterValues[item.name]?.split('-');
            if (filterValues[item.name]?.match(/^[\d-]+$/)) {
                value = value.map((v) => parseInt(v));
            }
            if (item.type === FilterItemTypes.List || item.type === FilterItemTypes.FilterableList) {
                item.active = !!value?.length;
                for (const option of item.options) {
                    option.selected = !!value?.includes(option.value);
                }
            }
            else if (item.type === FilterItemTypes.Flag) {
                item.active = value !== undefined;
            }
            else if (item.type === FilterItemTypes.Range) {
                if (value?.length) {
                    const [min, max] = value;
                    item.active = true;
                    item.value.min = parseInt(min);
                    item.value.max = parseInt(max);
                }
                else {
                    item.active = false;
                }
            }
            else if (item.type === FilterItemTypes.Text) {
                item.value = filterValues[item.name] || '';
                item.active = !!item.value;
            }
        }
    }
    function update(newItems) {
        const oldExpanded = Object.fromEntries(items.value.map(item => [item.name, item.expanded ?? false]));
        items.value = newItems.map(item => ({
            ...item,
            expanded: oldExpanded[item.name] ?? false,
        }));
        enabled.value = !!newItems.length;
    }
    function updateValues() {
        let result = {};
        for (const item of items.value) {
            if (item.type === FilterItemTypes.List || item.type === FilterItemTypes.FilterableList) {
                const options = [];
                for (const option of item.options) {
                    if (option.selected) {
                        options.push(option.value);
                    }
                }
                if (options.length) {
                    result[item.name] = options.join('-');
                    item.active = true;
                }
                else {
                    item.active = false;
                }
            }
            if (item.type === FilterItemTypes.Flag) {
                if (item.active) {
                    result[item.name] = true;
                }
            }
            if (item.type === FilterItemTypes.Range) {
                if (item.value.min === item.available.min && item.value.max === item.available.max) {
                    item.active = false;
                    continue;
                }
                if (item.value.min === 0 && item.value.max === 0) {
                    item.active = false;
                    continue;
                }
                result[item.name] = `${item.value.min}-${item.value.max}`;
                item.active = true;
            }
            if (item.type === FilterItemTypes.Text) {
                if (item.value && item.value.trim() !== '') {
                    result[item.name] = item.value.trim();
                    item.active = true;
                }
                else {
                    item.active = false;
                }
            }
        }
        values.value = result;
    }
    function updateAvailable(available) {
        for (const item of items.value) {
            if (item.type === FilterItemTypes.Flag) {
                item.available = available[item.name];
            }
            if (item.type === FilterItemTypes.Range) {
                item.available = available[item.name];
            }
            if (item.type === FilterItemTypes.List || item.type === FilterItemTypes.FilterableList) {
                for (const option of item.options) {
                    option.available = available[item.name]?.[option.name] ?? false;
                }
            }
        }
    }
    function setDefaultSort(defaultSort) {
        sort.value.default = defaultSort;
    }
    function setSortType(newSort) {
        sort.value.type = newSort;
        config.value.sort = newSort;
    }
    function setSortTypes(newSortTypes) {
        sort.value.types = newSortTypes;
    }
    function saveExpandedStateToSession() {
        const expanded = {};
        for (const item of items.value) {
            expanded[item.name] = !!item.expanded;
        }
        const state = {
            mode: config.value.mode,
            id: config.value.id,
            expanded,
        };
        sessionStorage.setItem('filterExpanded', JSON.stringify(state));
    }
    function restoreExpandedStateFromSession() {
        const raw = sessionStorage.getItem('filterExpanded');
        if (!raw)
            return;
        try {
            const state = JSON.parse(raw);
            // Проверяем, соответствует ли mode и id текущему фильтру
            if (state.mode !== config.value.mode || state.id !== config.value.id) {
                sessionStorage.removeItem('filterExpanded');
                return;
            }
            // Восстанавливаем состояние открытых фильтров
            for (const item of items.value) {
                item.expanded = state.expanded?.[item.name] ?? false;
            }
        }
        catch (e) {
            console.warn('Ошибка при восстановлении expanded', e);
            sessionStorage.removeItem('filterExpanded');
        }
    }
    return {
        // State
        config, values, instance,
        active, enabled, loaded,
        items, sort, available,
        // Methods
        disable, setLoaded, setInitialState,
        setValues, update, updateValues,
        setDefaultSort, setSortType, setSortTypes,
        saveExpandedStateToSession,
        restoreExpandedStateFromSession,
        updateAvailable,
    };
});
